/**
 * App-wide imperative confirmation (SH-9 / SW-14).
 *
 * `window.confirm` / `prompt` / `alert` block the page, cannot be styled,
 * tested or announced properly, and offer no typed confirmation. Components
 * that own their JSX should use `useConfirmDialog()`. For call sites that
 * cannot easily render an element (event handlers deep in a list row, API
 * helpers, hooks), `confirmAction` / `promptAction` show the same
 * ConfirmDialog through a single <ConfirmActionHost /> mounted in App.
 *
 *   onClick={async () => {
 *     if (await confirmAction(`Delete template "${t.name}"?`)) del.mutate(t.id);
 *   }}
 *
 *   const reason = await promptAction({ title: 'Cancel order', label: 'Reason' });
 *   if (reason !== null) cancel(reason);
 *
 * A plain string becomes a destructive-toned dialog whose title is the
 * question and whose confirm button is the leading verb ("Delete", "Remove").
 */
import { useEffect } from 'react';
import {
  useConfirmDialog,
  type ConfirmOptions,
  type PromptOptions,
} from '@/platform/design-system/hooks/useConfirmDialog';

type Handlers = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  promptText: (options: PromptOptions) => Promise<string | null>;
};

let host: Handlers | null = null;

const DESTRUCTIVE_VERBS = new Set([
  'delete',
  'remove',
  'disconnect',
  'deactivate',
  'disable',
  'cancel',
  'stop',
  'revoke',
  'reboot',
  'retract',
  'dismiss',
  'discard',
  'uninstall',
  'purge',
  'reset',
  'wipe',
]);

/** Turn a native-confirm style question into dialog options. */
export function confirmOptionsFromText(text: string): ConfirmOptions {
  const trimmed = text.trim();
  const end = trimmed.search(/[?.!](\s|$)/);
  const title = end >= 0 ? trimmed.slice(0, end + 1) : trimmed;
  const message = end >= 0 ? trimmed.slice(end + 1).trim() : '';
  const verb = /^[A-Za-z]+/.exec(title)?.[0] ?? '';
  const lower = verb.toLowerCase();
  const isQuestionVerb = ['are', 'is', 'do', 'should'].includes(lower);
  const destructive =
    DESTRUCTIVE_VERBS.has(lower) ||
    [...DESTRUCTIVE_VERBS].some((v) => new RegExp(`\\b${v}`, 'i').test(title));
  return {
    title,
    message,
    variant: destructive ? 'danger' : 'warning',
    confirmLabel: verb && !isQuestionVerb ? verb[0].toUpperCase() + verb.slice(1) : 'Confirm',
  };
}

/** Ask for confirmation. Resolves false on cancel / Escape. */
export function confirmAction(options: ConfirmOptions | string): Promise<boolean> {
  const resolved = typeof options === 'string' ? confirmOptionsFromText(options) : options;
  if (!host) {
    // No host mounted (an isolated component test): fall back to the
    // browser dialog so existing window.confirm stubs keep working.
    const text = [resolved.title, resolved.message].filter(Boolean).join('\n\n');
    if (resolved.typedConfirmation) {
      // ui-primitives-ok: fallback only when <ConfirmActionHost/> is absent
      const typed = window.prompt(`${text}\n\nType ${resolved.typedConfirmation} to confirm`);
      return Promise.resolve(typed?.trim() === resolved.typedConfirmation);
    }
    // ui-primitives-ok: fallback only when <ConfirmActionHost/> is absent
    return Promise.resolve(window.confirm(text));
  }
  return host.confirm(resolved);
}

/** Ask for a value (a reason, a name). Resolves null on cancel. */
export function promptAction(options: PromptOptions): Promise<string | null> {
  if (!host) {
    // ui-primitives-ok: fallback only when <ConfirmActionHost/> is absent
    const value = window.prompt(options.label ?? options.title, options.defaultValue);
    return Promise.resolve(value === null ? null : value.trim());
  }
  return host.promptText(options);
}

/** Mount once near the app root (App.tsx, next to the Toaster). */
export function ConfirmActionHost() {
  const { confirm, promptText, ConfirmDialogElement } = useConfirmDialog();
  useEffect(() => {
    const handlers = { confirm, promptText };
    host = handlers;
    return () => {
      if (host === handlers) host = null;
    };
  }, [confirm, promptText]);
  return ConfirmDialogElement;
}
